export const GIT_BASH_INSTALLER_FILENAME = 'Git-2.53.0.2-64-bit.exe'
const GIT_BASH_RELEASE_PATH = `v2.53.0.windows.2/${GIT_BASH_INSTALLER_FILENAME}`

export interface GitBashDownloadSource {
  name: string
  url: string
}

const GIT_BASH_DOWNLOAD_SOURCES: readonly GitBashDownloadSource[] = [
  {
    name: 'huaweicloud',
    url: `https://mirrors.huaweicloud.com/git-for-windows/${GIT_BASH_RELEASE_PATH}`,
  },
  {
    name: 'ustc',
    url: `https://mirrors.ustc.edu.cn/git-for-windows/${GIT_BASH_RELEASE_PATH}`,
  },
  {
    name: 'sjtug',
    url: `https://mirrors.sjtug.sjtu.edu.cn/git-for-windows/${GIT_BASH_RELEASE_PATH}`,
  },
]

export const GIT_BASH_MANUAL_DOWNLOAD_URL = 'https://git-scm.com/install/windows'
export const GIT_BASH_INSTALL_TIMEOUT_MS = 30 * 60 * 1000
export const GIT_BASH_AUTO_INSTALL_RETRY_DELAY_MS = 2_500
// Keep startup auto-install enabled, but use domestic mirrors instead of
// GitHub-first downloads so the Windows prerequisite flow still completes.
export const GIT_BASH_AUTOMATIC_INSTALL_ENABLED = true

export function getGitBashDownloadSources(): readonly GitBashDownloadSource[] {
  return GIT_BASH_DOWNLOAD_SOURCES
}

export interface GitBashInstallResult {
  success: boolean
  error?: string
}

export function shouldAttemptAutomaticGitBashInstall(options: {
  isPrereqScreen: boolean
  platform: 'win32' | 'darwin' | 'linux'
  found: boolean
  isInstalling: boolean
  hasAttempted: boolean
  enabled?: boolean
}): boolean {
  const { isPrereqScreen, platform, found, isInstalling, hasAttempted, enabled = GIT_BASH_AUTOMATIC_INSTALL_ENABLED } = options

  return enabled && isPrereqScreen && platform === 'win32' && !found && !isInstalling && !hasAttempted
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function isGitBashInstallDownloadError(message: string): boolean {
  return message.includes('net::ERR_')
    || message.startsWith('Download failed with HTTP')
    || message === 'Download timed out'
}

export function formatGitBashInstallError(error: unknown): string {
  const rawMessage = error instanceof Error ? error.message : 'Failed to install Git Bash'

  if (isGitBashInstallDownloadError(rawMessage)) {
    return `Failed to download Git for Windows automatically. Check your network connection or download it manually from ${GIT_BASH_MANUAL_DOWNLOAD_URL}`
  }

  return rawMessage
}

export function shouldRetryAutomaticGitBashInstall(message?: string): boolean {
  return !!message && isGitBashInstallDownloadError(message)
}

export async function installGitBashWithOptionalRetry<T extends GitBashInstallResult>(options: {
  install: () => Promise<T>
  source: 'auto' | 'manual'
  retryDelayMs?: number
  wait?: (ms: number) => Promise<void>
}): Promise<T> {
  const {
    install,
    source,
    retryDelayMs = GIT_BASH_AUTO_INSTALL_RETRY_DELAY_MS,
    wait = delay,
  } = options

  let result = await install()

  if (
    source === 'auto'
    && !result.success
    && shouldRetryAutomaticGitBashInstall(result.error)
  ) {
    await wait(retryDelayMs)
    result = await install()
  }

  return result
}
